<template>
  <div class="classroom-detail-page">
    <div class="bg-deco" aria-hidden="true">
      <div class="bg-deco-icon i1"><img src="@/assets/images/stars.png" alt="" /></div>
      <div class="bg-deco-icon i2"><img src="@/assets/images/cloud3.png" alt="" /></div>
      <div class="bg-deco-icon i3"><img src="@/assets/images/rainbow2.png" alt="" /></div>
      <div class="bg-deco-icon i4"><img src="@/assets/images/butterfly2.png" alt="" /></div>
    </div>

    <b-modal
      :active.sync="modalAssign"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal>
      <assign-classroom
        :id="id"
        :assignType="assignType"
        :existingTeacherIds="existingTeacherIds"
        :existingStudentIds="existingStudentIds"
        @over="onAssignOver"
      />
    </b-modal>

    <div v-if="fetchClassroom">
      <div class="page-header">
        <div class="header-left">
          <button class="back-btn" @click="$router.push({ name: 'classrooms' })" aria-label="Back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>
          <div>
            <h1 class="page-title">{{ fetchClassroom.name }}</h1>
            <p class="page-subtitle" v-if="fetchClassroom.remarks">{{ fetchClassroom.remarks }}</p>
          </div>
        </div>
        <div class="header-actions">
          <button
            class="btn-secondary"
            @click="$router.push({ name: 'view-categories', params: { grade_subject_id: fetchClassroom.grade_subject }, query: { classroom_id: id } })"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
            <span>View Categories</span>
          </button>
        </div>
      </div>

      <div class="panel">
        <div class="panel-header">
          <div>
            <h2 class="panel-title">
              <span class="dot dot-teacher"></span>
              Teachers
              <span class="count">{{ teachers.length }}</span>
            </h2>
            <p class="panel-sub">People assigned to teach this classroom</p>
          </div>
          <button v-if="canManage" class="btn-primary" @click="openAssign('T')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            <span>Assign Teachers</span>
          </button>
        </div>
        <div class="panel-body">
          <UpTable :hoverable="true" :data="teachers">
            <UpTableColumn label="Name" field="name" v-slot="props" sortable>
              <div class="cell-user">
                <span class="cell-avatar">{{ initials(props.row.name) }}</span>
                <span>{{ props.row.name }}</span>
              </div>
            </UpTableColumn>
            <UpTableColumn label="Email" field="email" v-slot="props" sortable>
              {{ props.row.email }}
            </UpTableColumn>
            <UpTableColumn v-if="canManage" label="Action" v-slot="props">
              <div class="row-actions">
                <button class="icon-btn icon-btn-danger" @click="removeUser(props.row, 'T')" title="Remove from classroom">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path></svg>
                </button>
              </div>
            </UpTableColumn>
            <template #empty>
              <div class="empty-state">
                <div class="empty-emoji">🎓</div>
                <div class="empty-text">No teachers assigned yet</div>
                <button v-if="canManage" class="btn-primary btn-sm" @click="openAssign('T')">Assign teachers</button>
              </div>
            </template>
          </UpTable>
        </div>
      </div>

      <div class="panel">
        <div class="panel-header">
          <div>
            <h2 class="panel-title">
              <span class="dot dot-student"></span>
              Students
              <span class="count">{{ students.length }}</span>
            </h2>
            <p class="panel-sub">Students enrolled in this classroom and their progress</p>
          </div>
          <button v-if="canManage" class="btn-primary" @click="openAssign('S')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            <span>Assign Students</span>
          </button>
        </div>
        <div class="panel-body">
          <UpTable :hoverable="true" :data="students">
            <UpTableColumn label="Name" field="name" v-slot="props" sortable>
              <div class="cell-user">
                <span class="cell-avatar cell-avatar-student">{{ initials(props.row.name) }}</span>
                <span>{{ props.row.name }}</span>
              </div>
            </UpTableColumn>
            <UpTableColumn label="Email" field="email" v-slot="props" sortable>
              {{ props.row.email }}
            </UpTableColumn>
            <UpTableColumn label="Skills" v-slot="props">
              <span class="metric">{{ analyticsValue(props.row, 'skills_practiced') }}</span>
            </UpTableColumn>
            <UpTableColumn label="Questions Solved" v-slot="props">
              <span class="metric">{{ analyticsValue(props.row, 'questions_attempted') }}</span>
            </UpTableColumn>
            <UpTableColumn label="Time" v-slot="props">
              <span class="metric">{{ analyticsValue(props.row, 'time_taken') }} min</span>
            </UpTableColumn>
            <UpTableColumn label="Action" v-slot="props">
              <div class="row-actions">
                <button
                  v-if="props.row.kid_id"
                  class="icon-btn icon-btn-primary"
                  @click="$router.push(`/kid/${props.row.kid_id}/analytics?grade_subject=${fetchClassroom.grade_subject}`)"
                  title="View analytics"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                </button>
                <button v-if="canManage" class="icon-btn icon-btn-danger" @click="removeUser(props.row, 'S')" title="Remove from classroom">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path></svg>
                </button>
              </div>
            </UpTableColumn>
            <template #empty>
              <div class="empty-state">
                <div class="empty-emoji">👩‍🎓</div>
                <div class="empty-text">No students enrolled yet</div>
                <button v-if="canManage" class="btn-primary btn-sm" @click="openAssign('S')">Assign students</button>
              </div>
            </template>
          </UpTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AssignClassroom from '@/components/School/AssignClassroom'

export default {
  name: 'ClassroomDetails',
  components: { AssignClassroom },
  props: {
    id: { type: String, required: true }
  },
  data() {
    return {
      modalAssign: false,
      assignType: 'T'
    }
  },
  computed: {
    ...mapGetters(['fetchClassroom', 'fetchClassroomStudents', 'getTeacherToken', 'getUserType']),
    canManage() {
      return this.getUserType === 'SA' || this.getUserType === 'A'
    },
    teachers() {
      return (this.fetchClassroom && this.fetchClassroom.teachers) || []
    },
    students() {
      return this.fetchClassroomStudents || []
    },
    existingTeacherIds() {
      return this.teachers.map(t => t._id)
    },
    existingStudentIds() {
      return this.students.map(s => s._id)
    }
  },
  methods: {
    ...mapActions(['loadClassroom', 'loadClassroomStudents']),
    openAssign(type) {
      this.assignType = type
      this.modalAssign = true
    },
    onAssignOver() {
      this.modalAssign = false
      this.refresh()
    },
    refresh() {
      this.loadClassroom({ token: this.getTeacherToken, id: this.id })
      this.loadClassroomStudents({ token: this.getTeacherToken, id: this.id, analytics: true })
    },
    initials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      const a = parts[0] ? parts[0][0] : ''
      const b = parts[1] ? parts[1][0] : ''
      return (a + b).toUpperCase() || '?'
    },
    analyticsValue(row, key) {
      if (!row || !row.analytics) return 0
      return row.analytics[key] || 0
    },
    removeUser() {
      this.$buefy.toast.open({
        message: 'Removing users is not supported yet',
        type: 'is-warning'
      })
    }
  },
  async created() {
    this.refresh()
  }
}
</script>

<style scoped>
.classroom-detail-page {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(1rem, 2.5vw, 2rem) clamp(0.75rem, 2vw, 1.5rem);
  min-height: 100vh;
  background:
    radial-gradient(1100px 480px at 50% -120px, rgba(137, 25, 231, 0.08), transparent 60%),
    radial-gradient(900px 420px at 100% 100%, rgba(49, 200, 255, 0.10), transparent 60%);
}

.classroom-detail-page::after {
  content: '';
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 38vh;
  max-height: 380px;
  background-image: url(~@/assets/images/cloud2.png);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: bottom center;
  pointer-events: none;
  z-index: 0;
  opacity: 0.85;
}

.bg-deco {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}
.bg-deco-icon {
  position: absolute;
  width: clamp(36px, 5vw, 64px);
  opacity: 0.55;
  animation: bg-float 6s ease-in-out infinite;
}
.bg-deco-icon img { width: 100%; height: auto; display: block; }
.bg-deco-icon.i1 { top: 6%;  left: 4%;  animation-delay: 0s; }
.bg-deco-icon.i2 { top: 10%; right: 6%; animation-delay: -1.5s; }
.bg-deco-icon.i3 { top: 50%; right: 2%; animation-delay: -2.2s; width: clamp(50px, 7vw, 90px); }
.bg-deco-icon.i4 { top: 70%; left: 2%;  animation-delay: -3s; }

@keyframes bg-float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50%      { transform: translateY(-12px) rotate(3deg); }
}

.page-header,
.panel {
  position: relative;
  z-index: 2;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.back-btn {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #ffffff;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}
.back-btn:hover {
  background: #1f2937;
  color: #ffffff;
  border-color: #1f2937;
  transform: translateX(-2px);
}

.page-title {
  font-size: clamp(1.5rem, 2.6vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 0.15rem;
  background: linear-gradient(135deg, #8919e7 0%, #4361ee 60%, #31c8ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  line-height: 1.1;
  word-break: break-word;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
  word-break: break-word;
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.6rem 1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.btn-primary {
  background: linear-gradient(135deg, #8919e7, #6b38c7);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(137, 25, 231, 0.28);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(137, 25, 231, 0.36);
}
.btn-primary.btn-sm {
  padding: 0.45rem 0.85rem;
  font-size: 0.85rem;
}
.btn-secondary {
  background: #ffffff;
  color: #475569;
  border: 1px solid rgba(15, 23, 42, 0.1);
}
.btn-secondary:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #1f2937;
  border-color: rgba(15, 23, 42, 0.18);
}

.panel {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  margin-bottom: 1.1rem;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
}
.panel-title {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}
.panel-title .count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 0.45rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.dot-teacher { background: #8919e7; }
.dot-student { background: #31c8ff; }
.panel-sub {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0.15rem 0 0;
}
.panel-body {
  padding: 1rem 1.25rem 1.25rem;
}

.cell-user {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
}
.cell-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8919e7, #6b38c7);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.02em;
}
.cell-avatar.cell-avatar-student {
  background: linear-gradient(135deg, #31c8ff, #4361ee);
}

.metric {
  font-weight: 600;
  color: #1f2937;
}

.row-actions {
  display: inline-flex;
  gap: 0.35rem;
  justify-content: flex-end;
  width: 100%;
}
.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.icon-btn-primary { color: #4361ee; }
.icon-btn-primary:hover {
  background: #4361ee;
  color: #ffffff;
  border-color: #4361ee;
}
.icon-btn-danger { color: #ef4444; }
.icon-btn-danger:hover {
  background: #ef4444;
  color: #ffffff;
  border-color: #ef4444;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 1.5rem 1rem;
}
.empty-emoji { font-size: 2rem; }
.empty-text {
  font-size: 0.95rem;
  color: #64748b;
}

@media screen and (max-width: 640px) {
  .header-actions {
    width: 100%;
  }
  .header-actions .btn-secondary {
    flex: 1;
    justify-content: center;
  }
  .panel-header {
    align-items: stretch;
  }
  .panel-header .btn-primary {
    width: 100%;
    justify-content: center;
  }
}
</style>
